
export const announceToScreenReader = (message: string) => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', 'polite');
  announcement.setAttribute('aria-atomic', 'true');
  announcement.setAttribute('class', 'sr-only');
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

export const trapFocus = (element: HTMLElement) => {
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstFocusable = focusableElements[0] as HTMLElement;
  const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;
  
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    }
  };
  
  element.addEventListener('keydown', handleKeyDown);
  firstFocusable?.focus();
  
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
};

export const getAriaLabel = (text: string, context?: string) => {
  return context ? `${text} ${context}` : text;
};

// iOS Safari specific accessibility fixes
export const enableIOSScrollFix = () => {
  if (typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
    // Prevent iOS Safari from zooming on input focus
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
      );
    }
    
    // Fix iOS Safari scroll issues
    (document.body.style as any).webkitOverflowScrolling = 'touch';
    document.body.style.touchAction = 'manipulation';
    
    // Ensure header is always accessible
    const header = document.querySelector('header');
    if (header) {
      header.style.position = 'relative';
      header.style.zIndex = '1000';
      header.style.webkitTransform = 'translate3d(0, 0, 0)';
      header.style.transform = 'translate3d(0, 0, 0)';
    }
  }
};

// Ensure proper touch targets for mobile accessibility
export const ensureTouchTargets = () => {
  if (typeof window === 'undefined' || window.innerWidth > 768) return;

  // `:not(svg *)` keeps this out of SVG content. Nodes inside an SVG scene
  // graph legitimately carry role="button", and forcing min-height/padding onto
  // them is meaningless (SVG ignores both) while the sweep's inline styles
  // clobber presentation the component is actively controlling.
  const interactiveElements = document.querySelectorAll(
    'button:not(svg *), a:not(svg *), [role="button"]:not(svg *), input, select, textarea'
  );

  // Phase 1: batch all layout reads up front to avoid interleaved
  // read/write cycles that trigger forced reflows (Lighthouse audit).
  const measurements: Array<{
    el: HTMLElement;
    height: number;
    width: number;
  }> = [];

  interactiveElements.forEach((element) => {
    const el = element as HTMLElement;
    const computedStyle = window.getComputedStyle(el);
    measurements.push({
      el,
      height: parseInt(computedStyle.height) || 0,
      width: parseInt(computedStyle.width) || 0,
    });
  });

  // Phase 2: perform all writes in a single animation frame so the
  // browser can coalesce them into one layout pass.
  requestAnimationFrame(() => {
    for (const { el, height, width } of measurements) {
      if (height < 44) {
        // Only pad vertically. This previously wrote the `padding` shorthand,
        // which as an inline style beat every class and collapsed asymmetric
        // horizontal padding — the search bar's `pl-12 pr-24` became 8px a
        // side, so its icons sat on top of the text. Horizontal padding is
        // irrelevant to reaching a 44px-tall touch target.
        const vertical = Math.max(0, (44 - height) / 2);
        el.style.minHeight = '44px';
        el.style.paddingTop = `${vertical}px`;
        el.style.paddingBottom = `${vertical}px`;
      }
      if (width < 44) {
        el.style.minWidth = '44px';
      }
      el.style.touchAction = 'manipulation';
      (el.style as any).webkitTapHighlightColor = 'transparent';
    }
  });
};
